import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { createTheme, Theme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/core/styles';
import { SnackbarProvider } from 'notistack';
import { InsightProvider, ToastProvider, AuthProvider, FetchProvider, UserProvider, NavProvider, SettingsProvider } from './components/contexts';
import { Layout, Routes } from './components/common';
import { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { _loginRequest } from './components/contexts/AuthContext';
import { _clientSettings } from './config/client-settings';
import { _sessionStorageKey } from './helpers/Utils';

const theme: Theme = createTheme({
    palette: {
        primary: {
            main: "#c2a07b"
        },
        secondary: {
            main: "#ff0000"
        },
        text: {
            secondary: "#c2a07b"
        }
    },
    overrides: {
        MuiStepIcon: {
            root: {
                '&$active': {
                    color: '#c2a07b',
                },
                '&$completed': {
                    color: '#d1d1d1',
                },
            },
        },
        MuiSwitch: {
        },
        MuiTabs: {
            indicator: {
                backgroundColor: "#c2a07b"
            }
        }
    }
});

type IApp = {
    baseUrl: string;
    msalInstance: IPublicClientApplication;
    isAccountLogged: boolean;
}

const App = ({ baseUrl, msalInstance, isAccountLogged }: IApp): JSX.Element => {
    const login = (): void => {
        msalInstance
        .initialize()
        .then(() => msalInstance.loginRedirect(_loginRequest))
        .catch(error => console.error(error));
    };

    useEffect((): void => {
        if (!isAccountLogged){
            //session storage is used as workaround since navigateToLoginRequestUrl of msal library is bugged
            if (
                !sessionStorage.getItem(_sessionStorageKey) &&
                window.location.href !== window.location.origin &&
                window.location.href.startsWith(_clientSettings.redirectUri) && //checking if url is related to this application
                !window.location.href.includes("/#code") //in deployed application, old browser tabs can reload application with token in url
            )
                sessionStorage.setItem(_sessionStorageKey, window.location.href);
            
            login();
        }
    }, []);

    return (
        <MsalProvider instance={msalInstance}>
            <BrowserRouter basename={baseUrl}>
                <InsightProvider>
                    <ThemeProvider theme={theme}>
                        <SnackbarProvider preventDuplicate maxSnack={5}>
                            <ToastProvider>
                                <AuthProvider>
                                    <FetchProvider>
                                        <SettingsProvider>
                                            <UserProvider>
                                                <NavProvider>
                                                    <Layout>
                                                        <Routes />
                                                    </Layout>
                                                </NavProvider>
                                            </UserProvider>
                                        </SettingsProvider>
                                    </FetchProvider>
                                </AuthProvider>
                            </ToastProvider>
                        </SnackbarProvider>
                    </ThemeProvider>
                </InsightProvider>
            </BrowserRouter>
        </MsalProvider>
    );
}

export default App;